/**
 * Position Sizer - Optimal Position Size Calculator
 *
 * Calculates position sizing based on account equity, risk tolerance,
 * and stop-loss levels. Includes R-multiple targets and Kelly Criterion.
 *
 * @version 1.0.0
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calculator,
  DollarSign,
  Target,
  Shield,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Check,
  Info,
  Percent,
  Crosshair,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface PositionSizerProps {
  equity?: number;
  currentPrice?: number;
  symbol?: string;
  onSizeCalculated?: (qty: number) => void;
}

interface CalculatedPosition {
  dollarRisk: number;
  perShareRisk: number;
  positionSize: number;
  totalValue: number;
  portfolioPercent: number;
  target1R: number;
  target2R: number;
  target3R: number;
}

// ============================================================================
// Helpers
// ============================================================================

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatNumber = (value: number, decimals = 2): string =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

// ============================================================================
// Component
// ============================================================================

export default function PositionSizer({
  equity: equityProp,
  currentPrice,
  symbol = '',
  onSizeCalculated,
}: PositionSizerProps) {
  // ---- State ----
  const [equity, setEquity] = useState<number>(equityProp ?? 100000);
  const [riskPercent, setRiskPercent] = useState<number>(2);
  const [entryPrice, setEntryPrice] = useState<number>(currentPrice ?? 0);
  const [stopLoss, setStopLoss] = useState<number>(0);
  const [kellyExpanded, setKellyExpanded] = useState<boolean>(false);
  const [winRate, setWinRate] = useState<number>(55);
  const [avgWinLoss, setAvgWinLoss] = useState<number>(1.5);

  // Sync props
  useEffect(() => {
    if (equityProp !== undefined) setEquity(equityProp);
  }, [equityProp]);

  useEffect(() => {
    if (currentPrice !== undefined && currentPrice > 0) {
      setEntryPrice(currentPrice);
      // Auto-set stop loss at 3% below entry if not yet set
      if (stopLoss === 0) {
        setStopLoss(parseFloat((currentPrice * 0.97).toFixed(2)));
      }
    }
  }, [currentPrice]);

  // ---- Calculations ----
  const calc = useMemo<CalculatedPosition | null>(() => {
    if (equity <= 0 || entryPrice <= 0 || stopLoss <= 0) return null;
    if (stopLoss >= entryPrice) return null;

    const dollarRisk = equity * (riskPercent / 100);
    const perShareRisk = entryPrice - stopLoss;
    if (perShareRisk <= 0) return null;

    const positionSize = Math.floor(dollarRisk / perShareRisk);
    const totalValue = positionSize * entryPrice;
    const portfolioPercent = (totalValue / equity) * 100;

    const target1R = entryPrice + perShareRisk;
    const target2R = entryPrice + perShareRisk * 2;
    const target3R = entryPrice + perShareRisk * 3;

    return {
      dollarRisk,
      perShareRisk,
      positionSize,
      totalValue,
      portfolioPercent,
      target1R,
      target2R,
      target3R,
    };
  }, [equity, riskPercent, entryPrice, stopLoss]);

  // Kelly Criterion
  const kellyPercent = useMemo<number>(() => {
    const p = winRate / 100;
    const q = 1 - p;
    const b = avgWinLoss;
    if (b <= 0 || q === 0) return 0;
    const kelly = (p * b - q) / b;
    return Math.max(0, kelly * 100);
  }, [winRate, avgWinLoss]);

  // ---- Handlers ----
  const handleApply = useCallback(() => {
    if (calc && onSizeCalculated) {
      onSizeCalculated(calc.positionSize);
    }
  }, [calc, onSizeCalculated]);

  // ---- Risk severity ----
  const riskColor =
    riskPercent <= 1
      ? 'text-green-400'
      : riskPercent <= 2
        ? 'text-cyan-400'
        : riskPercent <= 3
          ? 'text-yellow-400'
          : 'text-red-400';

  const riskBgColor =
    riskPercent <= 1
      ? 'bg-green-500/10 border-green-500/30'
      : riskPercent <= 2
        ? 'bg-cyan-500/10 border-cyan-500/30'
        : riskPercent <= 3
          ? 'bg-yellow-500/10 border-yellow-500/30'
          : 'bg-red-500/10 border-red-500/30';

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="bg-[#0b1022] border border-slate-700/60 rounded-xl p-5 max-w-md w-full font-sans">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
          <Calculator size={18} className="text-cyan-400" />
        </div>
        <div>
          <h2 className="text-white text-base font-semibold leading-tight">
            Position Sizer
            {symbol && (
              <span className="ml-2 text-xs font-medium text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">
                {symbol}
              </span>
            )}
          </h2>
          <p className="text-slate-500 text-xs mt-0.5">
            Calculate optimal position size
          </p>
        </div>
      </div>

      {/* Account Equity */}
      <div className="mb-4">
        <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-1.5 font-medium">
          <DollarSign size={12} />
          Account Equity
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
            $
          </span>
          <input
            type="number"
            value={equity || ''}
            onChange={(e) => setEquity(parseFloat(e.target.value) || 0)}
            className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 pl-7 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-colors"
            placeholder="100000"
          />
        </div>
        {equity > 0 && (
          <p className="text-slate-500 text-[11px] mt-1">
            {formatCurrency(equity)}
          </p>
        )}
      </div>

      {/* Risk Percentage Slider */}
      <div className="mb-4">
        <label className="flex items-center justify-between text-xs text-slate-400 mb-2 font-medium">
          <span className="flex items-center gap-1.5">
            <Shield size={12} />
            Risk Per Trade
          </span>
          <span className={`text-sm font-semibold ${riskColor}`}>
            {riskPercent.toFixed(1)}%
          </span>
        </label>
        <input
          type="range"
          min={0.5}
          max={5}
          step={0.1}
          value={riskPercent}
          onChange={(e) => setRiskPercent(parseFloat(e.target.value))}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-slate-700 accent-cyan-500
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-400
            [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(34,211,238,0.4)]
            [&::-webkit-slider-thumb]:cursor-pointer"
        />
        <div className="flex justify-between text-[10px] text-slate-600 mt-1">
          <span>0.5%</span>
          <span>2% (rec)</span>
          <span>5%</span>
        </div>
      </div>

      {/* Entry Price */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-1.5 font-medium">
            <Crosshair size={12} />
            Entry Price
          </label>
          <input
            type="number"
            value={entryPrice || ''}
            onChange={(e) => setEntryPrice(parseFloat(e.target.value) || 0)}
            step="0.01"
            className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-colors"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-1.5 font-medium">
            <AlertTriangle size={12} className="text-red-400" />
            Stop Loss
          </label>
          <input
            type="number"
            value={stopLoss || ''}
            onChange={(e) => setStopLoss(parseFloat(e.target.value) || 0)}
            step="0.01"
            className="w-full bg-[#0f172a] border border-red-500/30 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-red-400/50 focus:ring-1 focus:ring-red-500/20 transition-colors"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Validation Warning */}
      {entryPrice > 0 && stopLoss > 0 && stopLoss >= entryPrice && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">
          <AlertTriangle size={14} className="text-red-400 shrink-0" />
          <span className="text-xs text-red-300">
            Stop loss must be below entry price for a long position.
          </span>
        </div>
      )}

      {/* Calculated Outputs */}
      {calc && (
        <div className={`border rounded-lg p-3 mb-4 ${riskBgColor}`}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {/* Dollar Risk */}
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                Dollar Risk
              </p>
              <p className="text-sm font-semibold text-white">
                {formatCurrency(calc.dollarRisk)}
              </p>
            </div>
            {/* Per-Share Risk */}
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                Per-Share Risk
              </p>
              <p className="text-sm font-semibold text-white">
                {formatCurrency(calc.perShareRisk)}
              </p>
            </div>
            {/* Position Size */}
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                Position Size
              </p>
              <p className="text-lg font-bold text-cyan-400">
                {formatNumber(calc.positionSize, 0)}{' '}
                <span className="text-xs font-normal text-slate-500">
                  shares
                </span>
              </p>
            </div>
            {/* Total Value */}
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                Total Value
              </p>
              <p className="text-sm font-semibold text-white">
                {formatCurrency(calc.totalValue)}
              </p>
            </div>
            {/* Portfolio % */}
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                  % of Portfolio
                </p>
                <p className="text-sm font-semibold text-white">
                  {formatNumber(calc.portfolioPercent)}%
                </p>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-1.5 mt-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(calc.portfolioPercent, 100)}%`,
                    background:
                      calc.portfolioPercent > 50
                        ? '#ef4444'
                        : calc.portfolioPercent > 25
                          ? '#eab308'
                          : '#22d3ee',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* R-Multiple Targets */}
      {calc && (
        <div className="mb-4">
          <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-2 font-medium">
            <Target size={12} />
            R-Multiple Targets
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: '1R', value: calc.target1R, color: 'green' },
              { label: '2R', value: calc.target2R, color: 'cyan' },
              { label: '3R', value: calc.target3R, color: 'purple' },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className={`bg-${color}-500/10 border border-${color}-500/25 rounded-lg p-2 text-center`}
                style={{
                  backgroundColor: `color-mix(in srgb, ${
                    color === 'green'
                      ? '#22c55e'
                      : color === 'cyan'
                        ? '#06b6d4'
                        : '#a855f7'
                  } 8%, transparent)`,
                  borderColor: `color-mix(in srgb, ${
                    color === 'green'
                      ? '#22c55e'
                      : color === 'cyan'
                        ? '#06b6d4'
                        : '#a855f7'
                  } 25%, transparent)`,
                }}
              >
                <p
                  className="text-[10px] font-semibold mb-0.5"
                  style={{
                    color:
                      color === 'green'
                        ? '#4ade80'
                        : color === 'cyan'
                          ? '#22d3ee'
                          : '#c084fc',
                  }}
                >
                  {label}
                </p>
                <p className="text-xs text-white font-medium">
                  {formatCurrency(value)}
                </p>
                <p className="text-[10px] text-slate-500">
                  +{formatNumber(((value - entryPrice) / entryPrice) * 100)}%
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kelly Criterion (Collapsible) */}
      <div className="mb-4 border border-slate-700/60 rounded-lg overflow-hidden">
        <button
          onClick={() => setKellyExpanded(!kellyExpanded)}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-[#0f172a] hover:bg-slate-800/50 transition-colors"
        >
          <span className="flex items-center gap-2 text-xs text-slate-400 font-medium">
            <Percent size={12} />
            Kelly Criterion
            <span className="text-[10px] text-slate-600">(optional)</span>
          </span>
          <div className="flex items-center gap-2">
            {kellyPercent > 0 && (
              <span className="text-xs font-semibold text-amber-400">
                {formatNumber(kellyPercent)}%
              </span>
            )}
            {kellyExpanded ? (
              <ChevronUp size={14} className="text-slate-500" />
            ) : (
              <ChevronDown size={14} className="text-slate-500" />
            )}
          </div>
        </button>

        {kellyExpanded && (
          <div className="px-3 py-3 border-t border-slate-700/60 space-y-3">
            {/* Win Rate */}
            <div>
              <label className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Win Rate</span>
                <span className="text-white font-medium">{winRate}%</span>
              </label>
              <input
                type="range"
                min={20}
                max={80}
                step={1}
                value={winRate}
                onChange={(e) => setWinRate(parseInt(e.target.value))}
                className="w-full h-1 rounded-full appearance-none cursor-pointer bg-slate-700 accent-amber-500
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400
                  [&::-webkit-slider-thumb]:cursor-pointer"
              />
            </div>

            {/* Avg Win / Loss Ratio */}
            <div>
              <label className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Avg Win/Loss Ratio</span>
                <span className="text-white font-medium">
                  {avgWinLoss.toFixed(1)}:1
                </span>
              </label>
              <input
                type="range"
                min={0.5}
                max={5}
                step={0.1}
                value={avgWinLoss}
                onChange={(e) => setAvgWinLoss(parseFloat(e.target.value))}
                className="w-full h-1 rounded-full appearance-none cursor-pointer bg-slate-700 accent-amber-500
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400
                  [&::-webkit-slider-thumb]:cursor-pointer"
              />
            </div>

            {/* Kelly Result */}
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-400">
                  Kelly Optimal %
                </span>
                <span className="text-sm font-bold text-amber-400">
                  {formatNumber(kellyPercent)}%
                </span>
              </div>
              {kellyPercent > 0 && equity > 0 && (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-slate-400">
                    Half-Kelly (recommended)
                  </span>
                  <span className="text-xs font-semibold text-amber-300">
                    {formatCurrency(equity * (kellyPercent / 200))}
                  </span>
                </div>
              )}
              <div className="flex items-start gap-1.5 mt-2">
                <Info size={11} className="text-slate-600 mt-0.5 shrink-0" />
                <p className="text-[10px] text-slate-600 leading-relaxed">
                  Kelly Criterion suggests the mathematically optimal bet size.
                  Half-Kelly is widely recommended to reduce volatility.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Apply Button */}
      {onSizeCalculated && (
        <button
          onClick={handleApply}
          disabled={!calc}
          className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all duration-200
            ${
              calc
                ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900 shadow-lg shadow-cyan-500/20 cursor-pointer active:scale-[0.98]'
                : 'bg-slate-800 text-slate-600 cursor-not-allowed'
            }`}
        >
          <Check size={16} />
          Apply{calc ? ` ${formatNumber(calc.positionSize, 0)} Shares` : ''}
        </button>
      )}

      {/* Summary footer when no callback */}
      {!onSizeCalculated && calc && (
        <div className="flex items-center justify-center gap-2 bg-slate-800/50 border border-slate-700/40 rounded-lg px-3 py-2">
          <TrendingUp size={14} className="text-cyan-400" />
          <span className="text-xs text-slate-400">
            Recommended:{' '}
            <span className="text-white font-semibold">
              {formatNumber(calc.positionSize, 0)} shares
            </span>{' '}
            at {formatCurrency(entryPrice)}
          </span>
        </div>
      )}
    </div>
  );
}
